import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Platform } from 'react-native';
import { supabase, SUPABASE_URL } from '../utils/supabaseClient';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data.session || null);
    })();
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      setSession(newSession);
      if (_event === 'SIGNED_IN' && newSession?.user) {
        // Close any lingering auth modal when sign-in completes
        try { setAuthOpen(false); } catch (_) { }
        // Ensure profile exists on first sign-in (especially when email confirmation is enabled)
        const user = newSession.user;
        const { data: existing } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', user.id)
          .single();
        if (!existing?.id) {
          const meta = user.user_metadata || {};
          const payload = {
            id: user.id,
            name: meta.name || (user.email ? user.email.split('@')[0] : 'User'),
            email: user.email || meta.email,
            phone: meta.phone || '',
            company_name: meta.company_name || null,
          };
          await supabase.from('profiles').insert(payload);
        }
      }
    });
    return () => sub?.subscription?.unsubscribe?.();
  }, []);

  useEffect(() => {
    (async () => {
      if (!session?.user) {
        setProfile(null);
        return;
      }
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      if (!error && data) setProfile(data);
      else {
        // Attempt to create profile if missing (e.g., first login after email confirmation)
        const user = session.user;
        const { data: existing } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', user.id)
          .single();
        if (!existing?.id) {
          const meta = user.user_metadata || {};
          const payload = {
            id: user.id,
            name: meta.name || (user.email ? user.email.split('@')[0] : 'User'),
            email: user.email || meta.email,
            phone: meta.phone || '',
            company_name: meta.company_name || null,
          };
          await supabase.from('profiles').insert(payload);
          const { data: data2 } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();
          if (data2) setProfile(data2);
        }
      }
    })();
  }, [session]);

  // Auto-close the auth modal whenever a valid session becomes available
  useEffect(() => {
    if (session?.user && authOpen) {
      try { setAuthOpen(false); } catch (_) { }
    }
  }, [session?.user]);

  const openAuthModal = useCallback(() => setAuthOpen(true), []);
  const closeAuthModal = useCallback(() => setAuthOpen(false), []);

  const signUp = useCallback(async ({ email, password, name, phone, company }) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name, phone, company_name: company },
        },
      });
      if (error) throw error;
      const user = data.user;
      if (data.session && user) {
        // When email confirmation is disabled, we have a session immediately; ensure profile now
        const meta = user.user_metadata || {};
        const payload = {
          id: user.id,
          name: meta.name || name || (user.email ? user.email.split('@')[0] : 'User'),
          email: user.email || email,
          phone: meta.phone || phone || '',
          company_name: meta.company_name || company || null,
        };
        await supabase.from('profiles').insert(payload);
        setAuthOpen(false);
        setMessage('Signed up successfully.');
        return { ok: true, requiresEmailConfirm: false };
      }
      // Email confirmation required; tell user to verify before logging in
      setMessage('Account created. Please check your email to confirm your address before logging in.');
      return { ok: true, requiresEmailConfirm: true };
    } catch (e) {
      return { ok: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const signIn = useCallback(async ({ email, password }) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      setAuthOpen(false);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setLoading(true);
    try {
      const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/dashboard` : undefined;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo },
      });
      if (error) throw error;
      setMessage('Redirecting to Google…');
      // The browser will navigate away; on return, onAuthStateChange will update session
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    // Clear local UI state immediately so user sees logout
    setProfile(null);
    setSession(null);
    setAuthOpen(false);

    // Try to sign out from Supabase with a short timeout (don't let it hang)
    try {
      const signOutPromise = supabase.auth.signOut({ scope: 'global' });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Signout timeout')), 3000)
      );
      await Promise.race([signOutPromise, timeoutPromise]);
    } catch (e) {
      // Ignore errors - we'll clear local storage anyway
      console.log('Signout note:', e?.message);
    }

    // Clear all cached data aggressively
    try {
      if (typeof window !== 'undefined') {
        // Clear all relevant localStorage
        const keys = Object.keys(window.localStorage || {});
        const projectRef = SUPABASE_URL.replace('https://', '').split('.')[0];
        keys.forEach((k) => {
          const isDashboardCache = (k === 'dashboard_invoices_cache' || k.startsWith('invoice_prefetch_') || k.startsWith('quote_prefetch_'));
          const isSupabaseToken = (k.startsWith('sb-') && k.includes('-auth-token')) || k.includes(projectRef);
          const isGoTrue = k.startsWith('supabase.') || k.startsWith('gotrue.');
          if (isDashboardCache || isSupabaseToken || isGoTrue) {
            try { window.localStorage.removeItem(k); } catch (_) { }
          }
        });
        // Clear sessionStorage
        const sKeys = Object.keys(window.sessionStorage || {});
        sKeys.forEach((k) => {
          if (k.startsWith('sb-') || k.startsWith('supabase.') || k.startsWith('gotrue.')) {
            try { window.sessionStorage.removeItem(k); } catch (_) { }
          }
        });
      }
    } catch (_) { }

    // Force redirect on web - this is the key part
    if (Platform.OS === 'web') {
      window.location.href = '/';
    }
  }, []);

  const value = useMemo(() => ({
    session,
    profile,
    authOpen,
    openAuthModal,
    closeAuthModal,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    loading,
    message,
  }), [session, profile, authOpen, openAuthModal, closeAuthModal, signUp, signIn, signInWithGoogle, signOut, loading, message]);

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};